/**
 * Het overzicht (SPEC §7.2): aandacht nodig, de cijfers, de komende incasso's.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, metHerkansing, type Aandachtspunt, type Beheeroverzicht } from '../../api';
import { Datum, Euro, Kroon, Laden, Melding, useTitel } from '../../componenten/basis';
import { NIVEAUNAMEN, NIVEAUS } from '../../lib/niveau';

const SOORTNAAM: Record<Aandachtspunt['soort'], string> = {
  incasso_mislukt: 'Betaling mislukt',
  bijdrage_open: 'Bijdrage open',
  aanmelding_zonder_betaling: 'Nooit betaald',
  geen_mandaat: 'Geen mandaat',
};

export function Overzicht() {
  useTitel('Beheer');
  const [o, zetO] = useState<Beheeroverzicht | null>(null);
  const [fout, zetFout] = useState<string | null>(null);

  useEffect(() => {
    metHerkansing(() => api.haal<Beheeroverzicht>('/api/beheer/overzicht')).then(zetO).catch((f: Error) => zetFout(f.message));
  }, []);

  if (fout) return <Melding soort="fout">{fout}</Melding>;
  if (!o) return <Laden />;

  const urgent = o.aandacht.filter((a) => a.soort !== 'geen_mandaat');
  const zonderMandaat = o.aandacht.filter((a) => a.soort === 'geen_mandaat');

  return (
    <div className="space-y-8">
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tegel titel="Actieve helden" waarde={String(o.cijfers.actief)}>
          <span className="flex flex-wrap gap-3 text-xs">
            {NIVEAUS.map((n) => (
              <span key={n} className="text-inkt-zacht">
                {o.cijfers.perNiveau[n]} {NIVEAUNAMEN[n].toLowerCase()}
              </span>
            ))}
            {o.cijfers.bedrijven > 0 && <span className="text-inkt-zacht">waarvan {o.cijfers.bedrijven} {o.cijfers.bedrijven === 1 ? 'bedrijf' : 'bedrijven'}</span>}
          </span>
        </Tegel>
        <Tegel titel="Dit jaar ontvangen" waarde={<Euro cent={o.cijfers.ditJaarOntvangenCent} />} />
        <Tegel titel="Verwacht per jaar" waarde={<Euro cent={o.cijfers.verwachtKomendJaarCent} />}>
          <span className="text-xs text-inkt-zacht">bij de huidige actieve helden</span>
        </Tegel>
        <Tegel titel="Dit jaar" waarde={`+${o.cijfers.nieuwDitJaar} / −${o.cijfers.opgezegdDitJaar}`}>
          <span className="text-xs text-inkt-zacht">nieuw / opgezegd</span>
        </Tegel>
      </section>

      <section className="kaart p-5">
        <h2 className="text-xl">Aandacht nodig</h2>
        {urgent.length === 0 ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-groen">
            <Kroon className="h-4 w-4" /> Niets. Alles loopt.
          </p>
        ) : (
          <table className="tabel mt-3">
            <thead>
              <tr>
                <th>Wat</th>
                <th>Wie</th>
                <th>Sinds</th>
                <th>Bedrag</th>
              </tr>
            </thead>
            <tbody>
              {urgent.map((a, i) => (
                <tr key={i}>
                  <td>
                    <span className="font-medium">{SOORTNAAM[a.soort]}</span>
                    <span className="block text-xs text-inkt-zacht">{a.omschrijving}</span>
                  </td>
                  <td>
                    <Link to={`/beheer/helden/${a.held_id}`}>{a.naam}</Link>
                    <span className="block text-xs text-inkt-zacht">{a.email}</span>
                  </td>
                  <td className="text-inkt-zacht"><Datum waarde={a.sinds} /></td>
                  <td>{a.bedrag_cent !== null ? <Euro cent={a.bedrag_cent} /> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {zonderMandaat.length > 0 && (
          <p className="mt-4 text-sm text-inkt-zacht">
            {zonderMandaat.length} {zonderMandaat.length === 1 ? 'held heeft' : 'helden hebben'} geen incassomandaat en {zonderMandaat.length === 1 ? 'krijgt' : 'krijgen'} jaarlijks een betaalverzoek:{' '}
            {zonderMandaat.map((a, i) => (
              <span key={a.held_id}>
                {i > 0 && ', '}
                <Link to={`/beheer/helden/${a.held_id}`}>{a.naam}</Link>
              </span>
            ))}
            .
          </p>
        )}
      </section>

      <section className="kaart p-5">
        <h2 className="text-xl">Komende incasso's</h2>
        {o.komendeIncassos.length === 0 ? (
          <p className="mt-2 text-sm text-inkt-zacht">Nog geen helden.</p>
        ) : (
          <table className="tabel mt-3">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Held</th>
                <th>Bedrag</th>
                <th>Hoe</th>
              </tr>
            </thead>
            <tbody>
              {o.komendeIncassos.map((k) => (
                <tr key={k.held_id}>
                  <td><Datum waarde={k.datum} /></td>
                  <td>
                    <Link to={`/beheer/helden/${k.held_id}`}>{k.naam}</Link>
                  </td>
                  <td><Euro cent={k.bedrag_cent} /></td>
                  <td className="text-inkt-zacht">{k.mandaat ? 'incasso' : 'betaalverzoek'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Tegel({ titel, waarde, children }: { titel: string; waarde: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="kaart p-5">
      <p className="etiket">{titel}</p>
      <p className="font-display text-3xl text-goud-300">{waarde}</p>
      {children && <p className="mt-1">{children}</p>}
    </div>
  );
}
